import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Code as CodeIcon, ArrowDownward as ArrowDownwardIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';
import BackendCallDialog from '../components/BackendCallDialog';
import CardRouter from '../cards/CardRouter';


const FeaturesInfoColumn = () => {
  const { featuresResults, featuresLastCall } = useBuild();
  const [backendCallDialogOpen, setBackendCallDialogOpen] = useState(false);
  const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);

  const handleOpenBackendCallDialog = () => {
    setBackendCallDialogOpen(true);
  };

  const handleCloseBackendCallDialog = () => {
    setBackendCallDialogOpen(false);
  };

  const handleOpenViewUserDialog = () => {
    setViewUserDialogOpen(true);
  };

  const handleCloseViewUserDialog = () => {
    setViewUserDialogOpen(false);
  };

  const renderInfo = () => {
    // Show error message if there's an error in featuresResults
    if (featuresResults?.error) {
      return (
        <Stack spacing={2}>
          <Alert severity="error">
            <Typography variant="subtitle2" gutterBottom>
              Query Error
            </Typography>
            <Typography variant="body2">
              {featuresResults.error}
            </Typography>
          </Alert>
        </Stack>
      );
    }

    if (!featuresResults || !featuresResults.result) {
      return (
        <Typography variant="body2" color="text.secondary">
          No query executed yet.
        </Typography>
      );
    }

    const result = featuresResults.result;
    return (
      <Stack spacing={2}>
        {/* Backend Call Button */}
        <Button
          variant="outlined"
          startIcon={<CodeIcon />}
          onClick={handleOpenBackendCallDialog}
          fullWidth
          disabled={!featuresLastCall}
        >
          Backend Call
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <ArrowDownwardIcon color="action" />
        </Box>

        <Box>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Frontend</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {featuresResults.took_frontend}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Backend</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {result.took_backend}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">User Dynamo</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {result.took_dynamo_user}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Items Dynamo</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {result.took_dynamo_items}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Interactions Dynamo</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {result.took_dynamo_interactions}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Features</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {result.took_features}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Semantic</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {result.took_semantic}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Topics Matching</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {result.took_topics}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Topic Similarity</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {result.took_topic_similarity}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">User Affinity</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {result.took_user_affinity}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Clustering</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {result.took_clustering}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Items found</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {((result.hit_rate||0) * 100).toFixed(1)}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Items with embeds</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {((result.item_embed_rate||0) * 100).toFixed(1)}%
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Button
          variant="outlined"
          onClick={handleOpenViewUserDialog}
          fullWidth
          disabled={!result.user}
        >
          View User
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" gutterBottom>
          Information
        </Typography>
        
        <Box sx={{ mt: 2, flex: 1, overflow: 'auto' }}>
          {renderInfo()}
        </Box>

        {/* Backend Call Dialog */}
        <BackendCallDialog
          open={backendCallDialogOpen}
          onClose={handleCloseBackendCallDialog}
          apiCall={featuresLastCall}
          maxWidth="md"
        />
      </Paper>

      {/* View User Dialog */}
      <Dialog
        open={viewUserDialogOpen}
        onClose={handleCloseViewUserDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <CardRouter hit={featuresResults?.result?.user} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewUserDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeaturesInfoColumn;

